import React, { useEffect, useState } from 'react';
import {
  useDeleteInvoice,
  useGetInvoices,
  useGetInvoicesStates,
} from '../hooks/reactQuerys/InvoiceQueries';
import { LoadingSpinner } from '../core/LoadingSpinner';
import { NoDataContenct } from '../core/NoDataContenct';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, deDE, GridColDef } from '@mui/x-data-grid';
import {
  useGetLegalTransactionTypes,
  useSearchLegalTransactions,
} from '../hooks/data';
import { IinvoiceState } from '../Interfaces/IInvoiceState';
import { Delete, Warning } from '@mui/icons-material';
import { useTheme } from '@emotion/react';
import { useNavigate, useParams } from 'react-router-dom';
import { InvoiceDialog } from './InvoiceDialog';
import { InvoiceCsvImportDialog } from './InvoiceCsvImportDialog';
import { InvoiceCreateDialog } from './InvoiceCreateDialog';
import { useAlert } from '../context/AlertContext';
import { CustomeTextField } from '../generic/CustomeTextField';

export const InvoiceStateChip: React.FC<{
  idInvoiceState: number;
  invoiceStates: IinvoiceState[] | undefined;
}> = ({ idInvoiceState, invoiceStates }) => {
  const labelText = invoiceStates?.find(
    x => x.idInvoiceState === idInvoiceState
  )?.InvoiceState;

  if (!invoiceStates) {
    return <Chip label='Fehler' />;
  }

  if (idInvoiceState === 1) {
    return <Chip variant='outlined' color='info' label={labelText} />;
  } else if (idInvoiceState === 5) {
    return <Chip variant='outlined' color='success' label={labelText} />;
  } else if (idInvoiceState === 10) {
    return <Chip variant='filled' color='success' label={labelText} />;
  } else if (idInvoiceState === 15) {
    return <Chip variant='outlined' color='error' label={labelText} />;
  } else {
    return <Chip label={labelText} />;
  }
};

interface IFilterObject {
  idState: number;
  idType: number;
  from_date: string | null;
  to_date: string | null;
  searchTerm: string;
}

export const InvoiceOverview: React.FC = ({}) => {
  const theme = useTheme();
  const params = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const {
    legalTransactions,
    isLoadingLegalTransactions,
    abortSearchLegalTransactions,
  } = useSearchLegalTransactions();

  const { invoices, isLoadingInvoices, refetchInvoices } = useGetInvoices();
  const {
    legalTransactionTypes,
    isLoadingLegalTransactionTypes,
    refetchLegalTransactionTypes,
  } = useGetLegalTransactionTypes();
  const { invoiceStates, isLoadinginvoiceStates, refetchinvoiceStates } =
    useGetInvoicesStates();
  //
  const { deleteInvoice, isPendingDeleteInvoice } = useDeleteInvoice();
  const [idToDelte, setIdToDelte] = useState<number | null>(null);
  //
  const [filter, setFilter] = useState<IFilterObject>({
    idState: -1,
    idType: 0,
    from_date: null,
    to_date: null,
    searchTerm: '',
  });
  //
  const [idInvoice, setIdInvoice] = useState<number | null>(
    params.idInvoice ? Number(params.idInvoice) : null
  );
  //
  const [showClosed, setShowClosed] = useState(false);
  //
  const [isOpenDatevCsvImport, setIsOpenDatevCsvImport] = useState(false);

  const filteredInvoices =
    invoices === undefined
      ? []
      : invoices
          .filter(
            inv =>
              filter.idState === 0 ||
              (filter.idState === -1 &&
                invoiceStates?.find(
                  state => state.idInvoiceState === inv.idInvoiceState
                )?.isCloseing === false) ||
              inv.idInvoiceState === filter.idState
          )
          .filter(
            inv =>
              filter.from_date === null ||
              new Date(String(inv.Created_at)) >
                new Date(String(filter.from_date))
          )
          .filter(
            inv =>
              filter.to_date === null ||
              new Date(String(inv.Created_at)) <
                new Date(String(filter.to_date))
          )
          .filter(inv =>
            filter.searchTerm === ''
              ? true
              : (inv.Title === null || inv.Title === undefined
                  ? false
                  : filter.searchTerm
                      .toLocaleLowerCase()
                      .includes(inv.Title.toLocaleLowerCase()) ||
                    inv.Title.toLocaleLowerCase().includes(
                      filter.searchTerm.toLocaleLowerCase()
                    )) ||
                (inv.InvoiceNumber === null || inv.InvoiceNumber === undefined
                  ? false
                  : filter.searchTerm
                      .replace(' ', '')
                      .toLocaleLowerCase()
                      .includes(
                        inv.InvoiceNumber.replace(' ', '').toLocaleLowerCase()
                      ) ||
                    inv.InvoiceNumber.replace(' ', '')
                      .toLocaleLowerCase()
                      .includes(
                        filter.searchTerm.replace(' ', '').toLocaleLowerCase()
                      ))
          );
  useEffect(() => {
    setIdInvoice(params.idInvoice ? Number(params.idInvoice) : null);
  }, [params.idInvoice]);

  function checkDueStatus(inputDate: Date | string): string {
    const date =
      typeof inputDate === 'string' ? new Date(inputDate) : inputDate;
    const now = new Date();

    // Zwei Wochen in Millisekunden
    const twoWeeksInMs = 14 * 24 * 60 * 60 * 1000;

    if (isNaN(date.getTime())) {
      throw new Error('Ungültiges Datum');
    }

    return now.getTime() - date.getTime() > twoWeeksInMs ? 'Fällig' : 'Okay';
  }

  const handleDelete = async () => {
    if (idToDelte !== null) {
      try {
        await deleteInvoice(idToDelte);
        showAlert({
          severity: 'success',
          text: 'Erfolgreich gelöscht!',
        });
        refetchInvoices();
        setIdToDelte(null);
      } catch (error) {
        showAlert({
          severity: 'error',
          text: 'Es ist ein Fehler aufgetretten!',
        });
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'idLegalTransactionRecordNumber',
      headerName: '',
      width: 55,
      renderCell: params =>
        !params.value && (
          <Tooltip title='Diese Rechnung wurde keiner Urkundennummer zugeordnet!'>
            <Warning />
          </Tooltip>
        ),
    },
    {
      field: 'idInvoice',
      headerName: 'Nr.',
      width: 90,
    },

    {
      field: 'InvoiceNumber',
      headerName: 'Rechnungsnummer',
      width: 180,
    },
    {
      field: 'idInvoiceState',
      headerName: 'Status',
      renderCell: params => (
        <InvoiceStateChip
          idInvoiceState={Number(params.value)}
          invoiceStates={invoiceStates}
        />
      ),
      width: 120,
    },
    {
      field: 'Created_at',
      headerName: 'Fristung',
      width: 100,
      renderCell: params => checkDueStatus(params.value),
    },
    {
      field: 'LegalTransactionType',
      headerName: 'Art',
      width: 250,
    },

    {
      field: 'Title',
      headerName: 'Vorgang',
      flex: 1,
    },

    {
      field: 'Total',
      headerName: 'Wert',
      width: 150,
      renderCell: params =>
        Number(params.value).toLocaleString('de-DE', {
          style: 'currency',
          currency: 'EUR',
        }),
    },

    {
      field: 'Action',
      headerName: 'Aktionen',
      width: 100,
      renderCell: params => (
        <IconButton
          onClick={event => {
            event.stopPropagation();
            setIdToDelte(Number(params.row.idInvoice));
          }}
        >
          <Delete />
        </IconButton>
      ),
    },
  ];

  if (
    isLoadingInvoices ||
    isLoadingLegalTransactionTypes ||
    isLoadinginvoiceStates
  ) {
    return <LoadingSpinner />;
  }

  if (!invoices || !legalTransactionTypes || !invoiceStates) {
    return (
      <NoDataContenct
        refetch={() => {
          refetchInvoices();
          //refetchLegalClients();
          refetchLegalTransactionTypes();
          refetchinvoiceStates();
        }}
      />
    );
  }

  return (
    <>
      {idToDelte && (
        <Dialog open onClose={() => setIdToDelte(null)}>
          <DialogContent>
            <Typography>Wollen Sie die Rechnung entgültig löschen?</Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant='outlined'
              disabled={isPendingDeleteInvoice}
              onClick={handleDelete}
            >
              {isPendingDeleteInvoice ? (
                <Box display='flex' alignItems='center' gap={1}>
                  <span>Ja</span>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                'Ja'
              )}
            </Button>
            <Button
              variant='contained'
              disabled={isPendingDeleteInvoice}
              onClick={() => setIdToDelte(null)}
            >
              Nein
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {idInvoice !== null && (
        <InvoiceDialog
          idInvoice={idInvoice}
          invoiceStates={invoiceStates}
          legalTransactionTypes={legalTransactionTypes}
          onClose={() => {
            navigate('/invoices');
          }}
          onSave={() => {
            refetchInvoices();
            navigate('/invoices');
          }}
        />
      )}

      <Typography variant='h4'>Rechnungen</Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item md={2} xs={4}>
          <CustomeTextField
            attr='from_date'
            label='Vom'
            object={filter}
            setObject={setFilter}
            type='date'
            null
          />
        </Grid>
        <Grid item md={2} xs={4}>
          <CustomeTextField
            attr='to_date'
            label='Bis'
            object={filter}
            setObject={setFilter}
            type='date'
            null
          />
        </Grid>
        <Grid item md={2} xs={4}>
          <CustomeTextField
            attr='idState'
            label='Status'
            object={filter}
            setObject={setFilter}
            type='select'
          >
            {[
              { idInvoiceState: 0, InvoiceState: 'Alle' },
              { idInvoiceState: -1, InvoiceState: 'Nur Offene' },
              ...invoiceStates,
            ].map(state => (
              <MenuItem
                key={`fitlter-state-${state.idInvoiceState}`}
                value={state.idInvoiceState}
              >
                {state.InvoiceState}
              </MenuItem>
            ))}
          </CustomeTextField>
        </Grid>
        <Grid item md={4} xs={6}>
          <CustomeTextField
            attr='searchTerm'
            label='Suche (Rechnungsnummer, Vorgangstitel)'
            object={filter}
            setObject={setFilter}
            type='string'
          />
        </Grid>
        <Grid item md={2} xs={6}>
          <InvoiceCreateDialog refetchTrigger={() => refetchInvoices()} />
          <InvoiceCsvImportDialog
            invoices={invoices}
            invoiceStates={invoiceStates}
            legalClients={[]}
            legalTransactionTypes={legalTransactionTypes}
            refetchTrigger={() => refetchInvoices()}
          />
        </Grid>
      </Grid>
      <DataGrid
        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
        rows={filteredInvoices}
        columns={columns}
        getRowId={row => row.idInvoice}
        onRowClick={params => navigate(`/invoices/${params.id}`)}
      />
    </>
  );
};
